// ===================================================================================================
// PackagesLCL.jsx — Minimal, controlled LCL packages editor (memoized)
// - Two UI modes: "volumes" (quantity/volume/weight) and "dimensions" (WxHxL/qty/weight)
// - Stateless: receives values and emits patches via setters passed by props
// - Read-only mode disables interactions and renders current values
// ===================================================================================================

import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { MInputTextField, MSelect, MCheckBox } from "src/M_Components";

// RHF imports for local, field-level renders
import { useFormContext, Controller } from "react-hook-form";
import "./Packages.css"; // shared responsive layout for Packages* components

/** Safely access RHF context (component still works without FormProvider) */
function useOptionalFormContext() {
  try {
    return useFormContext();
  } catch {
    return null;
  }
}

function PackagesLCLInner({
  mode,
  setMode, // 'volumes' | 'dimensions'
  base,
  setBase, // 'plt' | 'box'
  packageTypes,
  volumes,
  setvolumes, // { quantity, volumeM3, weightKg }
  dimensions,
  setDimensions, // array (uses only index 0)
  readOnly = false,
}) {
  const { t } = useTranslation();

  // Resolve effective mode and helpers
  const effectiveMode = mode || "volumes";
  const isVolumes = effectiveMode !== "dimensions";

  const rhf = useOptionalFormContext();
  const rhfControl = rhf?.control ?? null;

  // -----------------------------------------------------------------------------------------------
  // RHF initial hydration from legacy values (run once)
  // -----------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!rhf) return;

    // Initialize mode/base if absent
    const curMode = rhf.getValues("lclMode");
    if (!curMode)
      rhf.setValue("lclMode", effectiveMode, { shouldDirty: false });

    const curBase = rhf.getValues("lclBase");
    if (!curBase)
      rhf.setValue("lclBase", base || "plt", { shouldDirty: false });

    // Initialize volumes
    const vq = rhf.getValues("lclvolumes.quantity");
    if (vq === undefined) {
      rhf.setValue("lclvolumes.quantity", volumes?.quantity ?? "", {
        shouldDirty: false,
      });
      rhf.setValue("lclvolumes.volumeM3", volumes?.volumeM3 ?? "", {
        shouldDirty: false,
      });
      rhf.setValue("lclvolumes.weightKg", volumes?.weightKg ?? "", {
        shouldDirty: false,
      });
    }

    // Initialize dimensions (index 0)
    const dq = rhf.getValues("lclDimensions.0.quantity");
    if (dq === undefined) {
      const d0 =
        Array.isArray(dimensions) && dimensions.length > 0
          ? dimensions[0]
          : { quantity: "", hCm: "", wCm: "", lCm: "", weightKg: "" };
      rhf.setValue("lclDimensions.0.quantity", d0.quantity ?? "", {
        shouldDirty: false,
      });
      rhf.setValue("lclDimensions.0.hCm", d0.hCm ?? "", { shouldDirty: false });
      rhf.setValue("lclDimensions.0.wCm", d0.wCm ?? "", { shouldDirty: false });
      rhf.setValue("lclDimensions.0.lCm", d0.lCm ?? "", { shouldDirty: false });
      rhf.setValue("lclDimensions.0.weightKg", d0.weightKg ?? "", {
        shouldDirty: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rhf]);

  // Volumes model (legacy controlled)
  const volumesValue = volumes || {};
  const applyVolumesLegacy = (next) => {
    if (typeof setvolumes === "function") setvolumes(next);
  };
  const updateVolumesLegacy = (patch) =>
    applyVolumesLegacy({ ...volumesValue, ...patch });

  // Single dimensions row (index 0); modeled as an array externally
  const dim0Legacy =
    Array.isArray(dimensions) && dimensions.length > 0
      ? dimensions[0]
      : { quantity: "", hCm: "", wCm: "", lCm: "", weightKg: "" };

  const updateDim0Legacy = (patch) => {
    if (typeof setDimensions !== "function") return;
    const next0 = { ...dim0Legacy, ...patch };
    setDimensions([next0]);
  };

  // Package type options:
  // - If API provided, map to 'plt'/'box' via Code heuristic
  // - Else, fallback to a simple static pair
  const baseOptions =
    Array.isArray(packageTypes) && packageTypes.length
      ? packageTypes.map((p) => {
          const txt = (p?.Code ?? p?.Name ?? "").toLowerCase();
          return {
            label: p?.Name || p?.Code || "-",
            value: txt.includes("box") ? "box" : "plt",
          };
        })
      : [
          { label: "Pallet (plt)", value: "plt" },
          { label: "Box (box)", value: "box" },
        ];

  // Switch mode with RHF + legacy sync
  const switchTo = useCallback(
    (m) => {
      if (readOnly) return;
      const next = m === "dimensions" ? "dimensions" : "volumes";

      if (rhf) {
        rhf.setValue("lclMode", next, { shouldDirty: true });
      }

      if (typeof setMode === "function") {
        setMode(next);
      }
    },
    [rhf, setMode, readOnly]
  );

  // Change base with RHF + legacy sync (select is discrete -> emit immediately)
  const changeBase = useCallback(
    (val) => {
      if (readOnly) return;
      const next = val || "plt";
      if (rhf) rhf.setValue("lclBase", next, { shouldDirty: true });
      if (typeof setBase === "function") setBase(next);
    },
    [rhf, setBase, readOnly]
  );

  // Helpers for blur emission for volumes/dimensions (text/number inputs)
  const emitVolumesBlur = useCallback(() => {
    if (!rhf || typeof setvolumes !== "function") return;
    const next = {
      quantity: rhf.getValues("lclvolumes.quantity") ?? "",
      volumeM3: rhf.getValues("lclvolumes.volumeM3") ?? "",
      weightKg: rhf.getValues("lclvolumes.weightKg") ?? "",
    };
    setvolumes(next);
  }, [rhf, setvolumes]);

  const emitDim0Blur = useCallback(() => {
    if (!rhf || typeof setDimensions !== "function") return;
    const next0 = {
      quantity: rhf.getValues("lclDimensions.0.quantity") ?? "",
      hCm: rhf.getValues("lclDimensions.0.hCm") ?? "",
      wCm: rhf.getValues("lclDimensions.0.wCm") ?? "",
      lCm: rhf.getValues("lclDimensions.0.lCm") ?? "",
      weightKg: rhf.getValues("lclDimensions.0.weightKg") ?? "",
    };
    setDimensions([next0]);
  }, [rhf, setDimensions]);

  return (
    <div className="packages-lcl-root">
      {/* Mode toggle: Volumes vs Dimensions */}
      <div className="packages-lcl-toggle">
        <MCheckBox
          label={t("Volumes")}
          checked={isVolumes}
          onChange={() => switchTo("volumes")}
          disabled={readOnly}
        />
        <MCheckBox
          label={t("Dimensions")}
          checked={!isVolumes}
          onChange={() => switchTo("dimensions")}
          disabled={readOnly}
        />
      </div>

      {/* Volumes UI */}
      {isVolumes && (
        <div className="packages-lcl-volumes-grid">
          {/* Package Type (select: discrete -> emit on change) */}
          {rhf ? (
            <Controller
              name="lclBase"
              control={rhfControl}
              render={({ field }) => (
                <MSelect
                  label={t("Package Type")}
                  options={baseOptions}
                  autocomplete
                  value={
                    baseOptions.find((o) => o.value === field.value) || null
                  }
                  onChangeValue={(sel) => {
                    field.onChange(sel?.value || "plt");
                    changeBase(sel?.value || "plt");
                  }}
                  disabled={readOnly}
                  noNone
                />
              )}
            />
          ) : (
            <MSelect
              label={t("Package Type")}
              options={baseOptions}
              autocomplete
              value={baseOptions.find((o) => o.value === base) || null}
              onChangeValue={(sel) =>
                typeof setBase === "function" && setBase(sel?.value || "plt")
              }
              disabled={readOnly}
              noNone
            />
          )}

          {/* Quantity (number) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclvolumes.quantity"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Quantity")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitVolumesBlur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Quantity")}
              value={volumesValue.quantity ?? ""}
              onChange={(e) =>
                updateVolumesLegacy({ quantity: e.target.value })
              }
              disabled={readOnly}
            />
          )}

          {/* Volume (m3) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclvolumes.volumeM3"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  label={t("Volume (m3)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitVolumesBlur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              label={t("Volume (m3)")}
              value={volumesValue.volumeM3 ?? ""}
              onChange={(e) =>
                updateVolumesLegacy({ volumeM3: e.target.value })
              }
              disabled={readOnly}
            />
          )}

          {/* Weight (KG) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclvolumes.weightKg"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Weight (KG)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitVolumesBlur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Weight (KG)")}
              value={volumesValue.weightKg ?? ""}
              onChange={(e) =>
                updateVolumesLegacy({ weightKg: e.target.value })
              }
              disabled={readOnly}
            />
          )}
        </div>
      )}

      {/* Dimensions UI */}
      {!isVolumes && (
        <div className="packages-lcl-dimensions-grid">
          {/* Package Type (select: discrete -> emit on change) */}
          {rhf ? (
            <Controller
              name="lclBase"
              control={rhfControl}
              render={({ field }) => (
                <MSelect
                  label={t("Package Type")}
                  options={baseOptions}
                  autocomplete
                  value={
                    baseOptions.find((o) => o.value === field.value) || null
                  }
                  onChangeValue={(sel) => {
                    field.onChange(sel?.value || "plt");
                    changeBase(sel?.value || "plt");
                  }}
                  disabled={readOnly}
                  noNone
                />
              )}
            />
          ) : (
            <MSelect
              label={t("Package Type")}
              options={baseOptions}
              autocomplete
              value={baseOptions.find((o) => o.value === base) || null}
              onChangeValue={(sel) =>
                typeof setBase === "function" && setBase(sel?.value || "plt")
              }
              disabled={readOnly}
              noNone
            />
          )}

          {/* Quantity - emit on blur */}
          {rhf ? (
            <Controller
              name="lclDimensions.0.quantity"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Quantity")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitDim0Blur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Quantity")}
              value={dim0Legacy.quantity ?? ""}
              onChange={(e) => updateDim0Legacy({ quantity: e.target.value })}
              disabled={readOnly}
            />
          )}

          {/* Height (cm) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclDimensions.0.hCm"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Height (cm)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitDim0Blur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Height (cm)")}
              value={dim0Legacy.hCm ?? ""}
              onChange={(e) => updateDim0Legacy({ hCm: e.target.value })}
              disabled={readOnly}
            />
          )}

          {/* Width (cm) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclDimensions.0.wCm"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Width (cm)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitDim0Blur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Width (cm)")}
              value={dim0Legacy.wCm ?? ""}
              onChange={(e) => updateDim0Legacy({ wCm: e.target.value })}
              disabled={readOnly}
            />
          )}

          {/* Length (cm) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclDimensions.0.lCm"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Length (cm)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitDim0Blur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Length (cm)")}
              value={dim0Legacy.lCm ?? ""}
              onChange={(e) => updateDim0Legacy({ lCm: e.target.value })}
              disabled={readOnly}
            />
          )}

          {/* Weight (KG) - emit on blur */}
          {rhf ? (
            <Controller
              name="lclDimensions.0.weightKg"
              control={rhfControl}
              render={({ field }) => (
                <MInputTextField
                  type="number"
                  label={t("Weight (KG)")}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={emitDim0Blur}
                  disabled={readOnly}
                />
              )}
            />
          ) : (
            <MInputTextField
              type="number"
              label={t("Weight (KG)")}
              value={dim0Legacy.weightKg ?? ""}
              onChange={(e) => updateDim0Legacy({ weightKg: e.target.value })}
              disabled={readOnly}
            />
          )}
        </div>
      )}
    </div>
  );
}

const PackagesLCL = React.memo(PackagesLCLInner);
export default PackagesLCL;

PackagesLCL.propTypes = {
  mode: PropTypes.oneOf(["volumes", "dimensions"]),
  setMode: PropTypes.func,
  base: PropTypes.oneOf(["plt", "box"]),
  setBase: PropTypes.func,
  packageTypes: PropTypes.array,
  volumes: PropTypes.object,
  setvolumes: PropTypes.func,
  dimensions: PropTypes.array,
  setDimensions: PropTypes.func,
  readOnly: PropTypes.bool,
};
